import { useRef, useState, useEffect } from 'react';
import { 
  Bold, Italic, Underline, List, ListOrdered, 
  Link, Image, Video, AlignLeft, AlignCenter, AlignRight,
  Heading1, Heading2, Quote, Minus, Undo, Redo, Code, Eye
} from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder = "Write your content here..." }) => {
  const editorRef = useRef(null);
  const isInternalUpdate = useRef(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  // Only sync the DOM from the `value` prop when the change came from an
  // external source (initial load, AI draft, switching back from HTML mode).
  // Skip it when the update originated from the user typing in this same
  // contentEditable div - otherwise resetting innerHTML on every keystroke
  // resets the caret to position 0 and reverses the typed text.
  useEffect(() => {
    if (!isHtmlMode && editorRef.current && !isInternalUpdate.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
    isInternalUpdate.current = false;
  }, [value, isHtmlMode]);

  const execCommand = (command, cmdValue = null) => {
    document.execCommand(command, false, cmdValue);
    editorRef.current?.focus();
    handleContentChange();
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      isInternalUpdate.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleHtmlChange = (e) => {
    onChange(e.target.value);
  };

  const toggleHtmlMode = () => {
    setIsHtmlMode(!isHtmlMode);
  };

  const insertLink = () => {
    if (linkUrl) {
      execCommand('createLink', linkUrl);
      setLinkUrl('');
      setShowLinkModal(false);
    }
  };

  const insertImage = () => {
    if (imageUrl) {
      const imgHtml = `<img src="${imageUrl}" alt="Image" style="max-width:100%;height:auto;max-height:400px;margin:1rem 0;border:1px solid #333;" />`;
      execCommand('insertHTML', imgHtml);
      setImageUrl('');
      setShowImageModal(false);
    }
  };

  const insertVideo = () => {
    if (videoUrl) {
      let embedHtml = '';
      if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
        const videoId = videoUrl.includes('youtu.be') 
          ? videoUrl.split('/').pop() 
          : new URL(videoUrl).searchParams.get('v');
        embedHtml = `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:1rem 0;"><iframe src="https://www.youtube.com/embed/${videoId}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;" allowfullscreen></iframe></div>`;
      } else if (videoUrl.includes('vimeo.com')) {
        const videoId = videoUrl.split('/').pop();
        embedHtml = `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:1rem 0;"><iframe src="https://player.vimeo.com/video/${videoId}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;" allowfullscreen></iframe></div>`;
      } else {
        embedHtml = `<video src="${videoUrl}" controls style="max-width:100%;height:auto;max-height:400px;margin:1rem 0;"></video>`;
      }
      execCommand('insertHTML', embedHtml);
      setVideoUrl('');
      setShowVideoModal(false);
    }
  };

  const ToolbarButton = ({ onClick, children, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-2 transition-colors ${active ? 'text-yellow-500 bg-[#222]' : 'text-gray-400 hover:text-white hover:bg-[#333]'}`}
    >
      {children}
    </button>
  );

  const ToolbarDivider = () => (
    <div className="w-px h-6 bg-[#333] mx-1" />
  );

  return (
    <div className="border border-[#222]" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0 p-2 border-b border-[#222] bg-[#0a0a0a]">
        {/* Mode Toggle */}
        <ToolbarButton 
          onClick={toggleHtmlMode} 
          title={isHtmlMode ? "Switch to Visual" : "Switch to HTML"} 
          active={isHtmlMode}
        >
          {isHtmlMode ? <Eye size={16} /> : <Code size={16} />}
        </ToolbarButton>
        
        <ToolbarDivider />

        {!isHtmlMode && (
          <>
            <ToolbarButton onClick={() => execCommand('bold')} title="Bold">
              <Bold size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('italic')} title="Italic">
              <Italic size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('underline')} title="Underline">
              <Underline size={16} />
            </ToolbarButton>

            <ToolbarDivider />

            <ToolbarButton onClick={() => execCommand('formatBlock', '<h2>')} title="Heading 1">
              <Heading1 size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('formatBlock', '<h3>')} title="Heading 2">
              <Heading2 size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('formatBlock', '<blockquote>')} title="Quote">
              <Quote size={16} />
            </ToolbarButton>

            <ToolbarDivider />

            <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
              <List size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Numbered List">
              <ListOrdered size={16} />
            </ToolbarButton>

            <ToolbarDivider />

            <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Align Left">
              <AlignLeft size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Align Center">
              <AlignCenter size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('justifyRight')} title="Align Right">
              <AlignRight size={16} />
            </ToolbarButton>

            <ToolbarDivider />

            <ToolbarButton onClick={() => setShowLinkModal(true)} title="Insert Link">
              <Link size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => setShowImageModal(true)} title="Insert Image">
              <Image size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => setShowVideoModal(true)} title="Insert Video">
              <Video size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('insertHorizontalRule')} title="Line">
              <Minus size={16} />
            </ToolbarButton>

            <ToolbarDivider />

            <ToolbarButton onClick={() => execCommand('undo')} title="Undo">
              <Undo size={16} />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('redo')} title="Redo">
              <Redo size={16} />
            </ToolbarButton>
          </>
        )}

        {isHtmlMode && (
          <span className="text-xs text-yellow-500 font-bold ml-2">HTML MODE - Paste raw HTML code here</span>
        )}
      </div>

      {/* Editor Area */}
      {isHtmlMode ? (
        <textarea
          value={value}
          onChange={handleHtmlChange}
          className="w-full min-h-[350px] p-4 bg-black text-green-400 font-mono text-sm focus:outline-none resize-none"
          placeholder="<p>Paste your HTML code here...</p>"
          data-testid="html-code-input"
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          className="min-h-[350px] p-4 bg-black text-gray-300 focus:outline-none"
          onInput={handleContentChange}
          onBlur={handleContentChange}
          data-placeholder={placeholder}
          data-testid="rich-text-content"
        />
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111] border border-[#222] p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-4">Insert Link</h3>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-4 py-3 bg-black border border-[#222] text-white focus:outline-none focus:border-yellow-500 mb-4"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 border border-[#222] text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={insertLink}
                className="px-4 py-2 bg-yellow-500 text-white hover:bg-yellow-600"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111] border border-[#222] p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-4">Insert Image</h3>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full px-4 py-3 bg-black border border-[#222] text-white focus:outline-none focus:border-yellow-500 mb-4"
              autoFocus
            />
            {imageUrl && (
              <div className="mb-4 border border-[#222] p-2">
                <img 
                  src={imageUrl} 
                  alt="Preview" 
                  className="max-w-full max-h-32 object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setShowImageModal(false); setImageUrl(''); }}
                className="px-4 py-2 border border-[#222] text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={insertImage}
                className="px-4 py-2 bg-yellow-500 text-white hover:bg-yellow-600"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111] border border-[#222] p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-4">Insert Video</h3>
            <p className="text-sm text-gray-500 mb-4">
              Supports YouTube, Vimeo, or direct video URLs
            </p>
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full px-4 py-3 bg-black border border-[#222] text-white focus:outline-none focus:border-yellow-500 mb-4"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setShowVideoModal(false); setVideoUrl(''); }}
                className="px-4 py-2 border border-[#222] text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={insertVideo}
                className="px-4 py-2 bg-yellow-500 text-white hover:bg-yellow-600"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #666;
          pointer-events: none;
        }
        [contenteditable] img {
          max-width: 100%;
          height: auto;
          max-height: 400px;
        }
        [contenteditable] h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 1.5rem 0 0.75rem;
          color: white;
        }
        [contenteditable] h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin: 1.25rem 0 0.5rem;
          color: white;
        }
        [contenteditable] blockquote {
          border-left: 4px solid #EAB308;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #999;
        }
        [contenteditable] ul, [contenteditable] ol {
          margin: 1rem 0;
          padding-left: 2rem;
        }
        [contenteditable] li {
          margin-bottom: 0.5rem;
        }
        [contenteditable] a {
          color: #EAB308;
          text-decoration: underline;
        }
        [contenteditable] hr {
          border: none;
          border-top: 1px solid #333;
          margin: 2rem 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
